// Подписка устройства на Web Push. Здесь и только здесь мы разговариваем с
// браузерным API; компонент знает лишь состояние.
// unsupported — само устройство/окно не умеет Web Push (webview внутри другого
// приложения, не-Safari браузер на iOS, не-HTTPS origin). not_configured —
// браузер способен, но на сервере не заданы ключи VAPID: это наша недоработка,
// а не вина человека, поэтому у состояния свой, честный текст (см. PushToggle.vue).
// worker_failed — браузер способен, VAPID настроен, но регистрация service worker'а
// не удалась (pwa.js глушит эту ошибку намеренно, см. её комментарий) или зависла:
// это тоже не вина человека и не «неспособный браузер» — отдельный честный текст.
import { reachGoal } from './metrika'

// Подписку включают из четырёх мест, а полоса внизу живёт в персистентном
// AppLayout: она монтируется один раз за загрузку и сама о чужом включении не
// узнает. На /profile/passes полоса и плашка видны одновременно, поэтому
// расхождение гарантированное: включил из плашки — полоса продолжает предлагать
// то же самое, а повторное нажатие шлёт цель Метрики push_enabled второй раз.
// Тот же приём, что в lib/install.ts: смену состояния объявляем событием окна,
// смонтированные потребители по нему перезапрашивают pushState().
export const PUSH_CHANGED_EVENT = 'push:changed'

export type PushState = 'unsupported' | 'not_configured' | 'ios_install_required' | 'denied' | 'worker_failed' | 'off' | 'on'

// Сколько ждём готовности worker'а, прежде чем считать регистрацию проваленной.
// navigator.serviceWorker.ready никогда не разрешится, если register() в pwa.js
// упал (или до сих пор не завершился) — без потолка pushState() висела бы вечно,
// и человек навсегда видел бы стартовое состояние тумблера.
const WORKER_READY_TIMEOUT_MS = 4000

function serviceWorkerReady(): Promise<ServiceWorkerRegistration | null> {
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), WORKER_READY_TIMEOUT_MS)),
  ])
}

// applicationServerKey принимает только байты, а ключ VAPID приезжает в
// base64url — отсюда ручная распаковка.
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(padded)
  const bytes = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i)
  return bytes
}

function vapidKey(): string | null {
  return document.querySelector<HTMLMetaElement>('meta[name="vapid-public-key"]')?.content ?? null
}

// Текущий пользователь — из того же JSON, из которого Inertia поднимает первую
// страницу. config.use_script_element_for_initial_page (config/initializers/
// inertia_rails.rb) кладёт этот JSON НЕ атрибутом data-page на div#app (сам div
// пустой), а отдельным <script data-page="app" type="application/json">.
// Форму этой разметки охраняет spec/requests/inertia_page_props_spec.rb:
// сменится она — упадёт тест, а не тихо перестанет работать ресинк.
function signedIn(): boolean {
  const json = document.querySelector('script[data-page]')?.textContent
  if (!json) return false

  try {
    return Boolean((JSON.parse(json) as { props?: { current_user?: unknown } }).props?.current_user)
  } catch {
    return false
  }
}

function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
}

// На iOS подписка возможна ТОЛЬКО из web app, добавленного на домашний экран.
// В обычной вкладке Safari pushManager есть, но подписка отваливается — поэтому
// различаем это состояние заранее и объясняем человеку, а не показываем ошибку.
function iosNeedsInstall(): boolean {
  return isIos() && window.matchMedia('(display-mode: standalone)').matches === false
}

export async function pushState(): Promise<PushState> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported'
  if (!vapidKey()) return 'not_configured'
  if (iosNeedsInstall()) return 'ios_install_required'
  if (Notification.permission === 'denied') return 'denied'

  const registration = await serviceWorkerReady()
  if (!registration) return 'worker_failed'

  const subscription = await registration.pushManager.getSubscription()
  return subscription ? 'on' : 'off'
}

// Ресинк endpoint при загрузке приложения. Браузер ротирует подписку сам
// (обновление приложения, вытеснение хранилища, ротация ключей), а на сервер она
// уходила РОВНО ОДИН РАЗ — при включении тумблера. После ротации сервер продолжал
// бить в мёртвый endpoint, а тумблер честно показывал «включено»: подписка в
// браузере есть, просто другая. Upsert по endpoint идемпотентен
// (PushSubscription.upsert_for!), поэтому повторная отправка безопасна.
// Обработчика pushsubscriptionchange у нас нет, и это его осознанная замена —
// docs/superpowers/backlog/2026-08-29-push-subscription-change-handler.md.
//
// Зовётся РОВНО ОДИН РАЗ за загрузку, из entrypoints/application.ts, и потому
// вынесена из pushState(): состояние спрашивают четыре точки входа, а плашка в
// колокольчике монтируется на каждое открытие выпадашки. Ресинк внутри pushState()
// означал бы POST на каждое из них, а у роута лимит 10 запросов в минуту
// (push_subscriptions_controller.rb) — десяток открытий колокольчика съедали бы
// его, и следующая честная подписка получила бы 429.
//
// Сбой глушим: человек ничего не нажимал, подписка в браузере жива, и неудачный
// ресинк ни на что в интерфейсе влиять не должен.
export async function resyncSubscription(): Promise<void> {
  try {
    // Зовётся на КАЖДОЙ загрузке бандла, включая публичные страницы. Без
    // текущего пользователя сервер подписку не примет, отказ здесь глушится, и
    // единственным следом остаётся съеденный лимит роута
    // (push_subscriptions_controller.rb ключует его по remote_ip для гостей).
    if (!signedIn()) return
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return
    if (!vapidKey()) return

    const registration = await serviceWorkerReady()
    if (!registration) return

    const subscription = await registration.pushManager.getSubscription()
    if (!subscription) return

    await syncSubscription(subscription)
  } catch {
    // Сеть, битый CSRF, отвалившийся worker — молча, см. комментарий выше.
  }
}

// Отправка подписки на сервер — общая для включения тумблера и для ресинка при
// загрузке. Ответ отдаётся вызывающему: реакция на отказ у путей разная.
function syncSubscription(subscription: PushSubscription): Promise<Response> {
  const json = subscription.toJSON()
  return fetch('/push_subscriptions', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
    body: JSON.stringify({ endpoint: subscription.endpoint, p256dh: json.keys?.p256dh, auth: json.keys?.auth }),
  })
}

export async function subscribePush(): Promise<PushState> {
  // Разрешение спрашиваем строго внутри обработчика клика — вызов вне жеста
  // пользователя Safari отклоняет молча.
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off'

  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidKey() as string),
  })

  const response = await syncSubscription(subscription)
  if (!response.ok) {
    // Сервер подписку не принял — оставлять её в браузере нельзя, иначе
    // устройство считает себя подписанным, а уведомлений не будет никогда.
    await subscription.unsubscribe()
    throw new Error(`push subscribe failed: ${response.status}`)
  }

  // Цель шлём здесь, а не в компонентах: включить уведомления можно из полосы, из
  // трёх плашек и тумблером в профиле, а точка успеха у всех одна. Считаем факт
  // принятой сервером подписки, а не факт нажатия кнопки.
  reachGoal('push_enabled')

  notifyChanged()

  return 'on'
}

export async function unsubscribePush(): Promise<PushState> {
  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()
  if (!subscription) return 'off'

  // Снимаем подписку на СЕРВЕРЕ первым и отписываем браузер только при успехе —
  // симметрично subscribePush(), которая при !response.ok откатывает браузерную
  // подписку. Обратный порядок при сбое DELETE (сеть, 500, битый CSRF) оставил бы
  // сервер думать, что подписка жива, а браузер — что уже отписан: пуши продолжили
  // бы сыпаться на устройство, которое считает себя выключенным. 404 — тоже успех:
  // сервер и так не хранит эту запись, это ровно то состояние, которого добивались.
  const response = await fetch('/push_subscriptions', {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
    body: JSON.stringify({ endpoint: subscription.endpoint }),
  })
  if (!response.ok && response.status !== 404) {
    throw new Error(`push unsubscribe failed: ${response.status}`)
  }

  await subscription.unsubscribe()
  notifyChanged()
  return 'off'
}

function notifyChanged(): void {
  window.dispatchEvent(new Event(PUSH_CHANGED_EVENT))
}

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
}
